import { Badge, BadgeProps } from '@gx-design/badge';
import { Button } from '@gx-design/button';
import { DropdownMenu, DropdownV2 } from '@gx-design/dropdown';
import { Icon, IconProps } from '@gx-design/icon';
import clsx from 'clsx';
import React, { FC, forwardRef, PropsWithChildren } from 'react';

import { Skeleton } from 'gtx-react/components/Skeleton';
import { formatDate } from 'lib/datefns';

const stopPropagation = (e: React.MouseEvent<HTMLElement, MouseEvent>) =>
    e.stopPropagation();

type TimelineProps = PropsWithChildren<{
    // actionClassName?: string;
    // actionIcon?: IconProps['name'];
    // actionLabel?: string;
    // actionHandler?: () => void;
    actionBar?: React.ReactNode;
}>;
const Timeline = (props: TimelineProps) => (
    <div className="crm-timeline">
        {/* <button className={props.actionClassName} onClick={props.actionHandler}>
            {props.actionIcon && <Icon name={props.actionIcon} />}
            {props.actionLabel && (
                <span className="gx-sr-only">{props.actionLabel}</span>
            )}
        </button> */}
        {props.actionBar}
        {props.children}
    </div>
);

type TimelineDayProps = PropsWithChildren<{ date?: Date }>;
const Day: FC<TimelineDayProps> = ({ date, children }) => (
    <section className="crm-timeline-item__day">
        {date instanceof Date && (
            <time
                className="gx-display-subtitle crm-timeline__date"
                dateTime={formatDate(date, 'yyyy-MM-dd')}
            >
                {formatDate(date, 'd MMMM yyyy')}
            </time>
        )}
        <ol>{children}</ol>
    </section>
);

export type ItemProps = PropsWithChildren<DotProps>;
const Item = ({ children, ...props }: ItemProps) => (
    <li className="crm-timeline-item">
        <Dot {...props} />
        {children}
    </li>
);

const Log: FC<PropsWithChildren> = ({ children }) => (
    <article className="crm-lead-activityLog">{children}</article>
);

export type CardAction = {
    onClick: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    iconName: IconProps['name'];
    text?: string;
    disabled?: boolean;
    actionId?: string;
};
type DropdownAction = Required<Omit<CardAction, 'disabled' | 'actionId'>> & {
    disabled?: boolean;
    actionId?: string;
};
export type CardProps = PropsWithChildren<{
    title?: string | React.ReactNode;
    classNameSuffix?: string;
    badgeText?: string;
    badgeStyle?: BadgeProps['style'];
    actions?: Array<CardAction | DropdownAction[]>;
    isOpen?: boolean;
    onOpen?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    onTransitionEnd?: React.TransitionEventHandler<HTMLElement>;
    showHeaderSkeleton?: React.ReactNode;
}>;
type CardComponent = React.ForwardRefExoticComponent<
    CardProps & React.RefAttributes<HTMLDivElement>
>;
const Card = forwardRef<HTMLElement, CardProps>(({ isOpen, ...props }, ref) => (
    <article
        className={clsx(
            'crm-lead-card',
            {
                [`crm-lead-card--${props.classNameSuffix}`]:
                    props.classNameSuffix,
            },
            { 'is-open': isOpen },
            { 'is-closed': !isOpen }
        )}
        ref={ref}
        onTransitionEnd={props?.onTransitionEnd}
    >
        <header className="crm-lead-card__header" onClick={props.onOpen}>
            <div className="crm-lead-card__info">
                {props.title && (
                    <h1 className="crm-lead-card__title gx-title-2">
                        {props.title}
                    </h1>
                )}
                {props.badgeText && (
                    <Badge text={props.badgeText} style={props.badgeStyle} />
                )}
                {props.showHeaderSkeleton && (
                    <>
                        <Skeleton width={160} height={16} />
                        <Skeleton width={100} height={24} />
                    </>
                )}
            </div>
            <div className="crm-lead-card__actions gx-flex-gap-sm">
                {props.actions?.map((elem, idx) =>
                    Array.isArray(elem) ? (
                        <DropdownMenu
                            key={`card-action-${idx}`}
                            buttonIsIconOnly
                            buttonContent={<Icon name="ellipsis" />}
                            buttonVariant="ghost"
                            buttonSize="small"
                            onTriggerClick={stopPropagation}
                        >
                            <DropdownV2.List>
                                {elem.map(({ iconName, ...action }, i) => (
                                    <DropdownV2.ListItem
                                        key={`card-action-${i}`}
                                        {...action}
                                        startElement={<Icon name={iconName} />}
                                        isDisabled={action.disabled ?? false}
                                    />
                                ))}
                            </DropdownV2.List>
                        </DropdownMenu>
                    ) : (
                        <Button
                            key={`card-action-${idx}`}
                            onClick={elem.onClick}
                            variant="ghost"
                            size="small"
                            iconOnly={Boolean(elem.text)}
                            disabled={elem.disabled}
                        >
                            <Icon name={elem.iconName} />
                            {elem.text && <span>{elem.text}</span>}
                        </Button>
                    )
                )}
                <Button
                    variant="ghost"
                    size="small"
                    iconOnly
                    onClick={props.onOpen}
                    className="crm-lead-card__caret"
                >
                    <Icon name="arrow-down" />
                </Button>
            </div>
        </header>
        <div className="crm-lead-card__content">{props.children}</div>
    </article>
)) as CardComponent;
Card.displayName = 'Card';

type DotProps = {
    iconName?: IconProps['name'];
    classNameSuffix?: string;
};
const Dot = (props: DotProps) => (
    <div
        className={clsx('crm-timeline-item__category', {
            [`crm-timeline-item__category--${props.classNameSuffix}`]: Boolean(
                props.classNameSuffix
            ),
        })}
    >
        {props.iconName && <Icon name={props.iconName} />}
    </div>
);

Item.Dot = Dot;
Item.Log = Log;
Item.Card = Card;
Timeline.Item = Item;
Timeline.Day = Day;
export { Timeline };
